//Built-in libraries

import { yupResolver } from '@hookform/resolvers/yup';
//STP Libraries
import { Stages as ProjectStages, Status as ProjectStatuses } from '@stptech/shared-kit/build/constants/project';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
//Third party libraries
import * as Yup from 'yup';
//Custom components
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { patchProject } from '../../../../../../../api/Sherpa/Project';
//Assets and Miscellaneous
import type { IPatchProjectServicePayload } from '../../../../../../../api/Sherpa/Project/modules/ProjectCore/types';
import { SherpaProjectReactQueryKey } from '../../../../../../../constants/ReactQueryKeys';
import { useProject } from '../../../../../../../hooks/ReactQueryHooks/useProjectQuery';
//Services
import { notifyUser } from '../../../../../../../store/Toast';
import type { TProjectStages, TProjectStatuses } from '../../../../../../../types/Project';
import { convertEnumToText, sanitizeResponseError } from '../../../../../../../utils';

//Component Begin
const validationSchema = Yup.object({
  projectStatus: Yup.string()
    .required('Project status is required')
    .transform((v) => (v.value ? v.value : undefined)),
  projectStage: Yup.string()
    .required('Project stage is required')
    .transform((v) => (v.value ? v.value : undefined)),
});

interface IProjectStatusFormData {
  projectStatus: TProjectStatuses;
  projectStage: TProjectStages;
}

interface IChangeProjectStatusProps {
  handleModal: () => void;
}

const ProjectStatusForm = ({ handleModal }: IChangeProjectStatusProps) => {
  const { projectId } = useParams<string>();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const projectStageValue = useWatch({ name: 'projectStage' });

  const updateProjectStatus = useMutation(
    (data: Partial<IPatchProjectServicePayload>) => patchProject({ projectId: Number(projectId), data }),
    {
      onSuccess: () => {
        handleModal();
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project status change successful',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const onSubmit = (formData: IProjectStatusFormData) => {
    const data = {
      status: formData.projectStatus,
      stage: formData.projectStage,
    };
    updateProjectStatus.mutate(data);
  };

  const currentProjectStageStatuses = ProjectStatuses[projectStageValue.value as TProjectStages];

  return (
    <HookForm onSubmit={onSubmit}>
      <h2 className='m-0'>Change Project Status</h2>
      <div className='my-4 space-y-4'>
        <HookReactSingleSelect
          id='change-project-stage'
          name='projectStage'
          label='Project Stage'
          options={Object.keys(ProjectStages).map((k) => ({
            label: ProjectStages[k as keyof typeof ProjectStages],
            value: k,
          }))}
        />
        {projectStageValue.value && (
          <HookReactSingleSelect
            id='change-project-status'
            name='projectStatus'
            label='Project Status'
            options={Object.keys(currentProjectStageStatuses).map((k) => ({
              label: currentProjectStageStatuses[k as keyof typeof currentProjectStageStatuses],
              value: k,
            }))}
          />
        )}
      </div>
      <div className='flex justify-end gap-4 mt-5'>
        <Button label='Cancel' btnStyle='btn-neutral' type='button' onClick={handleModal} />
        <Button
          label={`${updateProjectStatus.isLoading ? 'Saving...' : 'Save'}`}
          btnStyle='btn-primary'
          type='submit'
          disabled={updateProjectStatus.isLoading}
        />
      </div>
    </HookForm>
  );
};

const ChangeProjectStatus = (props: IChangeProjectStatusProps) => {
  const { projectId } = useParams<string>();
  const fetchProject = useProject({ projectId: Number(projectId) });
  const currentProjectStage = fetchProject?.data?.data?.stage;
  const currentProjectStatus = fetchProject?.data?.data?.status;

  const currentProjectStageStatuses = ProjectStatuses[currentProjectStage as TProjectStages];

  const transformedDefaultValues = {
    projectStatus: {
      label: currentProjectStageStatuses
        ? currentProjectStageStatuses[currentProjectStatus as keyof typeof currentProjectStageStatuses]
        : convertEnumToText(currentProjectStatus || ''),
      value: currentProjectStatus,
    },
    projectStage: {
      label: currentProjectStage ? ProjectStages[currentProjectStage as TProjectStages] : '',
      value: currentProjectStage ? currentProjectStage : '',
    },
  };

  return (
    <Modal>
      <HookFormProvider defaultValues={transformedDefaultValues} resolver={yupResolver(validationSchema) as any} mode='all'>
        <ProjectStatusForm {...props} />
      </HookFormProvider>
    </Modal>
  );
};

export default ChangeProjectStatus;
